import { Controller } from '@hotwired/stimulus'

declare const $: {
  (selector: HTMLElement): {
    on: (event: string, handler: () => void) => void
  }
}

// Connects to data-controller="wizard-v2--file-setup"
export default class extends Controller {
  static targets = [
    'form',
    'syncType',
    'fakeSyncType',
    'setSyncSection',
    'ledgersPerYearSection',
    'premiumNotice',
    'yearlyRadio',
    'quarterlyRadio',
    'monthlyRadio',
    'reportByGroup',
    'tooltipIcon',
    'quickbooksButton',
    'regularButton',
    'syncBannerQuickbooks',
    'syncBannerRegular',
  ]
  static values = {
    soleTraderTooltip: String,
  }

  declare readonly formTarget: HTMLFormElement
  declare readonly syncTypeTarget: HTMLElement
  declare readonly fakeSyncTypeTarget: HTMLElement
  declare readonly setSyncSectionTarget: HTMLElement
  declare readonly ledgersPerYearSectionTarget: HTMLElement
  declare readonly premiumNoticeTarget: HTMLElement
  declare readonly yearlyRadioTarget: HTMLInputElement
  declare readonly quarterlyRadioTarget: HTMLInputElement
  declare readonly monthlyRadioTarget: HTMLInputElement
  declare readonly hasPremiumNoticeTarget: boolean
  declare readonly hasSetSyncSectionTarget: boolean
  declare readonly hasLedgersPerYearSectionTarget: boolean
  declare readonly hasYearlyRadioTarget: boolean
  declare readonly hasQuarterlyRadioTarget: boolean
  declare readonly hasMonthlyRadioTarget: boolean
  declare readonly reportByGroupTarget: HTMLElement
  declare readonly hasReportByGroupTarget: boolean
  declare readonly tooltipIconTarget: HTMLElement
  declare readonly hasTooltipIconTarget: boolean
  declare readonly soleTraderTooltipValue: string
  declare readonly quickbooksButtonTarget: HTMLElement
  declare readonly regularButtonTarget: HTMLElement
  declare readonly hasQuickbooksButtonTarget: boolean
  declare readonly hasRegularButtonTarget: boolean
  declare readonly syncBannerQuickbooksTarget: HTMLElement
  declare readonly syncBannerRegularTarget: HTMLElement
  declare readonly hasSyncBannerQuickbooksTarget: boolean
  declare readonly hasSyncBannerRegularTarget: boolean

  connect(): void {
    const companyTypeSelect = this.formTarget.querySelector(
      'select[name="company[company_type]"]'
    ) as HTMLSelectElement
    if (companyTypeSelect) {
      this.handleCompanyTypeState(companyTypeSelect.value)

      // Use jQuery to listen for Select2 events
      $(companyTypeSelect).on('change', () => {
        this.handleCompanyTypeState(companyTypeSelect.value)
      })
    }

    const syncEntitySelect = this.formTarget.querySelector(
      'select[name="ledger_import_configuration[sync_entity_id]"]'
    ) as HTMLSelectElement

    if (syncEntitySelect) {
      this.handleSyncEntityChange(syncEntitySelect)

      $(syncEntitySelect).on('change', () => {
        this.handleSyncEntityChange(syncEntitySelect)
      })
    }
  }

  private handleSyncEntityChange(syncEntitySelect: HTMLSelectElement): void {
    const selectedOption =
      syncEntitySelect.options[syncEntitySelect.selectedIndex]
    const selectedText = selectedOption?.text || ''

    const isQuickBooks = selectedText.toLowerCase().includes('quickbooks')

    this.toggleSyncBanner(isQuickBooks)
    this.toggleSubmitButton(isQuickBooks)
  }

  private toggleSyncBanner(isQuickBooks: boolean): void {
    if (
      !this.hasSyncBannerQuickbooksTarget ||
      !this.hasSyncBannerRegularTarget
    ) {
      return
    }

    this.syncBannerQuickbooksTarget.classList.toggle('sf-hidden', !isQuickBooks)
    this.syncBannerRegularTarget.classList.toggle('sf-hidden', isQuickBooks)
  }

  private toggleSubmitButton(isQuickBooks: boolean): void {
    if (!this.hasQuickbooksButtonTarget || !this.hasRegularButtonTarget) {
      return
    }

    this.quickbooksButtonTarget.classList.toggle('sf-hidden', !isQuickBooks)
    this.regularButtonTarget.classList.toggle('sf-hidden', isQuickBooks)
  }

  private handleCompanyTypeState(companyType: string): void {
    const isPersonal = companyType === 'personal'
    const isSoleTrader = companyType === 'sole_trader'

    // Handle sync entity section visibility for consolidation (fallback to querySelector)
    const syncEntitySection = this.formTarget.querySelector(
      '.sync-entity-section'
    ) as HTMLElement
    if (syncEntitySection) {
      syncEntitySection.classList.toggle(
        'hidden',
        companyType === 'consolidation'
      )
    }

    // Handle premium notice visibility
    if (this.hasPremiumNoticeTarget) {
      const companyTypeSelect = this.formTarget.querySelector(
        'select[name="company[company_type]"]'
      ) as HTMLSelectElement
      if (companyTypeSelect) {
        const selectedOption =
          companyTypeSelect.options[companyTypeSelect.selectedIndex]
        const isPremium = selectedOption?.dataset?.label === 'Premium'
        this.premiumNoticeTarget.classList.toggle('sf-hidden', !isPremium)
      }
    }

    // Handle sections that should be hidden/disabled for personal companies
    if (this.hasSetSyncSectionTarget) {
      this.setSyncSectionTarget.classList.toggle('sf-hidden', isPersonal)
      this.toggleSectionInputs(this.setSyncSectionTarget, !isPersonal)
    }

    if (this.hasLedgersPerYearSectionTarget) {
      this.ledgersPerYearSectionTarget.classList.toggle('sf-hidden', isPersonal)
      this.toggleSectionInputs(this.ledgersPerYearSectionTarget, !isPersonal)

      // Handle sole trader ledgers per year restrictions
      this.handleSoleTraderLedgersPerYearRestriction(isSoleTrader)
    }
  }

  private toggleSectionInputs(section: HTMLElement, enabled: boolean): void {
    const inputs = section.querySelectorAll<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >('input, select, textarea')

    inputs.forEach((input) => {
      input.disabled = !enabled
    })
  }

  private handleSoleTraderLedgersPerYearRestriction(
    isSoleTrader: boolean
  ): void {
    if (this.hasQuarterlyRadioTarget) {
      this.quarterlyRadioTarget.disabled = isSoleTrader
      const quarterlyLabel = this.quarterlyRadioTarget.closest('label')
      if (quarterlyLabel) {
        quarterlyLabel.classList.toggle('sf-btn-disabled', isSoleTrader)
        quarterlyLabel.style.opacity = isSoleTrader ? '0.5' : '1'
        quarterlyLabel.style.pointerEvents = isSoleTrader ? 'none' : 'auto'
      }
    }

    if (this.hasMonthlyRadioTarget) {
      this.monthlyRadioTarget.disabled = isSoleTrader
      const monthlyLabel = this.monthlyRadioTarget.closest('label')
      if (monthlyLabel) {
        monthlyLabel.classList.toggle('sf-btn-disabled', isSoleTrader)
        monthlyLabel.style.opacity = isSoleTrader ? '0.5' : '1'
        monthlyLabel.style.pointerEvents = isSoleTrader ? 'none' : 'auto'
      }
    }

    // Show/hide tooltip icon based on sole trader status
    if (this.hasTooltipIconTarget) {
      this.tooltipIconTarget.classList.toggle('sf-hidden', !isSoleTrader)
    }

    // If sole trader is selected and quarterly/monthly is currently selected,
    // switch to yearly
    if (isSoleTrader && this.hasYearlyRadioTarget) {
      const quarterlyChecked =
        this.hasQuarterlyRadioTarget && this.quarterlyRadioTarget.checked
      const monthlyChecked =
        this.hasMonthlyRadioTarget && this.monthlyRadioTarget.checked

      if (quarterlyChecked || monthlyChecked) {
        this.yearlyRadioTarget.checked = true
      }
    }
  }

  changeCompanyType(e: Event): void {
    const target = e.target as HTMLSelectElement
    this.handleCompanyTypeState(target.value)
  }

  changeSyncType(e: Event): void {
    const target = e.target as HTMLSelectElement | HTMLInputElement
    if (target.value === 'consolidation') {
      this.syncTypeTarget.classList.add('sf-hidden')
      this.fakeSyncTypeTarget.classList.remove('sf-hidden')
    } else {
      this.syncTypeTarget.classList.remove('sf-hidden')
      this.fakeSyncTypeTarget.classList.add('sf-hidden')
    }

    // Hide all elements with class "sync-type-details" inside this.formTarget
    const syncTypeDetails =
      this.formTarget.querySelectorAll<HTMLElement>('.sync-type-details')
    syncTypeDetails.forEach((elem) => {
      elem.classList.add('sf-hidden')
    })

    // Show all elements with class matching `${target.value}-section` inside this.formTarget
    const targetSectionSelector = `.${target.value}-section`
    const targetSections = this.formTarget.querySelectorAll<HTMLElement>(
      targetSectionSelector
    )
    targetSections.forEach((elem) => {
      elem.classList.remove('sf-hidden')
    })
  }

  toggleSyncDateInput(e: Event): void {
    const checkbox = e.target as HTMLInputElement
    const syncDateInput = this.formTarget.querySelector(
      '.sync-date-input'
    ) as HTMLElement
    const dateField = syncDateInput.querySelector(
      'input[type="text"]'
    ) as HTMLInputElement

    if (checkbox.checked) {
      syncDateInput.classList.remove('sf-hidden')
      if (dateField && !dateField.value) {
        const currentYear = new Date().getFullYear()
        const defaultDate = `31/12/${currentYear - 1}`
        dateField.value = defaultDate
      }
    } else {
      syncDateInput.classList.add('sf-hidden')
      if (dateField) {
        dateField.value = ''
      }
    }
  }
}
